import useAsync, { AsyncState } from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

export interface UserOption {
  ref: string;
  label: string;
  /** Azure Entra object id, set by the msgraph provider. Absent on users not synced from Entra. */
  entraId?: string;
}

function displayNameOf(entity: Entity): string {
  const profile = entity.spec?.profile;
  const isProfileObject =
    !!profile && typeof profile === 'object' && !Array.isArray(profile);
  if (isProfileObject && typeof profile.displayName === 'string') {
    return profile.displayName;
  }
  return entity.metadata.title ?? entity.metadata.name;
}

// All catalog User entities, for the "pick a person" pickers used when
// creating a team or adding a member.
export function useCatalogUserOptions(): AsyncState<UserOption[]> {
  const catalogApi = useApi(catalogApiRef);
  return useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'User' },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.annotations',
        'spec.profile',
      ],
    });
    return items
      .map((entity: Entity): UserOption => ({
        ref: stringifyEntityRef(entity),
        label: displayNameOf(entity),
        entraId: entity.metadata.annotations?.['graph.microsoft.com/user-id']
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalogApi]);
}
